import { createContext, useContext, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { projectApis } from '../apis/projectApis';
import { handleErrors } from '../helpers';
import useToast from '../hooks/useToast';
import { PRIVATE_ROUTE } from '../routes/routes';

export const ProjectContext = createContext();

const ProjectProvider = ({ children }) => {
  const { projectId } = useParams();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);

  const getProject = async () => {
    setError(false);
    setLoading(true);
    try {
      const { data: projectRes } = await projectApis.getProject(projectId);
      setProject(projectRes);
    } catch (error) {
      setError(true);
      handleErrors(navigate, error, showToast);
    } finally {
      setLoading(false);
    }
  };

  // The get project endpoint is subject to a check on the backend, to see if the user is a part of the project.
  // If they are not, it will redirect to a 403. Because of this, we don't run the get project on routes that
  // are project-scoped, but the user is not yet a member of. This list is used to manage that.
  const excludeList = [PRIVATE_ROUTE.PROJECT_MEMBER_INVITATION];
  const contextNotUsedPage = excludeList.some((route) =>
    matchPath(route, location.pathname)
  );

  useEffect(() => {
    if (!projectId || contextNotUsedPage) return;
    getProject();
  }, [projectId]);

  return (
    <ProjectContext.Provider
      value={{ error, project, loading, refreshProject: getProject }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('There was an error fetching your project');
  }
  return context;
};

ProjectProvider.propTypes = {
  children: PropTypes.node,
};

export default ProjectProvider;
